import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, Ship, MapPin, Package, Calendar, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import html2pdf from "html2pdf.js";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  booked: "Booked",
  on_water: "On Water",
  at_port: "At Port",
  in_transit: "In Transit",
  received: "Received"
};

export default function PrintShipment() {
  const { id, businessSlug } = useParams();
  const [shipment, setShipment] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API = `${BASE_API}/b/${businessSlug}`;

  // Allow pinch-zoom on this page
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalContent = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=0.5, maximum-scale=3, user-scalable=yes');
    }
    return () => {
      if (viewport && originalContent) {
        viewport.setAttribute('content', originalContent);
      }
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [shipmentRes, businessRes] = await Promise.all([
        axios.get(`${API}/wholesale/shipments/${id}`),
        axios.get(`${API}/info`)
      ]);
      setShipment(shipmentRes.data);
      setBusinessInfo(businessRes.data);
    } catch (error) {
      console.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [id, API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('printable-shipment');
    const containerNum = shipment?.container_number || 'shipment';
    
    const opt = {
      margin: 0.25,
      filename: `Shipment-${containerNum}-${businessInfo?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'ILKSOY'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Shipment not found</p>
        <Link to={`/${businessSlug}/wholesale`}>
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  const business = businessInfo || {};

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Action Bar - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/${businessSlug}/wholesale`} className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Link>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
            <Button onClick={handlePrint} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-4xl mx-auto p-4 print:p-0 print:max-w-none">
        <div 
          id="printable-shipment" 
          className="bg-white shadow-lg print:shadow-none rounded-lg print:rounded-none overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6 print:p-4">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold">{business.name || "ILKSOY GROUP, LLC"}</h1>
                <p className="text-slate-300 text-sm mt-1">{business.category_label || "Import & Distribution"}</p>
                {business.address && <p className="text-slate-400 text-xs mt-2">{business.address}</p>}
                {business.phone && <p className="text-slate-400 text-xs">{business.phone}</p>}
                {business.email && <p className="text-slate-400 text-xs">{business.email}</p>}
              </div>
              <div className="text-right">
                <div className="flex items-center justify-end gap-2 mb-2">
                  <Ship className="w-6 h-6" />
                  <span className="text-xl font-semibold">SHIPMENT</span>
                </div>
                <p className="text-slate-300 text-sm">Container: {shipment.container_number || "N/A"}</p>
                <p className="text-slate-400 text-xs mt-1">Date: {formatDate(shipment.shipment_date || shipment.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                shipment.status === 'received' ? 'bg-green-100 text-green-800' :
                shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                shipment.status === 'at_port' ? 'bg-yellow-100 text-yellow-800' :
                shipment.status === 'on_water' ? 'bg-cyan-100 text-cyan-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {STATUS_LABELS[shipment.status] || shipment.status}
              </span>
            </div>
            <div className="text-right text-sm text-gray-500">
              Created: {formatDateTime(shipment.created_at)}
            </div>
          </div>

          {/* Main Details */}
          <div className="p-6 grid grid-cols-2 gap-6">
            {/* Left Column - Shipping Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-600" />
                Shipping Documents
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Container #:</span>
                  <span className="font-medium">{shipment.container_number || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Bill of Lading:</span>
                  <span className="font-medium">{shipment.bill_of_lading || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Customs Entry #:</span>
                  <span className="font-medium">{shipment.customs_entry_number || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Route Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-600" />
                Route Information
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Origin Country:</span>
                  <span className="font-medium">{shipment.origin_country || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Origin Port:</span>
                  <span className="font-medium">{shipment.origin_port || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Destination Port:</span>
                  <span className="font-medium">{shipment.destination_port || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Final Destination:</span>
                  <span className="font-medium">{shipment.final_destination || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dates & Cargo Section */}
          <div className="px-6 pb-6 grid grid-cols-2 gap-6">
            {/* Dates */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-600" />
                Important Dates
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipment Date:</span>
                  <span className="font-medium">{formatDate(shipment.shipment_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Est. Arrival:</span>
                  <span className="font-medium">{formatDate(shipment.estimated_arrival)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Actual Arrival:</span>
                  <span className="font-medium">{formatDate(shipment.actual_arrival)}</span>
                </div>
              </div>
            </div>

            {/* Cargo */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                <Package className="w-5 h-5 text-cyan-600" />
                Cargo Details
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Cartons:</span>
                  <span className="font-medium text-lg">{shipment.total_cartons || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Units:</span>
                  <span className="font-medium text-lg">{shipment.total_units || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones Timeline */}
          {shipment.milestones && shipment.milestones.length > 0 && (
            <div className="px-6 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
                Tracking History
              </h3>
              <div className="space-y-3">
                {shipment.milestones.map((milestone, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-3 h-3 rounded-full mt-1.5 ${
                      idx === 0 ? 'bg-cyan-500' : 'bg-gray-300'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="font-medium text-gray-800 capitalize">
                          {milestone.status?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(milestone.timestamp)}
                        </span>
                      </div>
                      {milestone.notes && (
                        <p className="text-sm text-gray-600 mt-0.5">{milestone.notes}</p>
                      )}
                      {milestone.location && (
                        <p className="text-xs text-gray-500 mt-0.5">Location: {milestone.location}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {shipment.notes && (
            <div className="px-6 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Notes</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{shipment.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center text-xs text-gray-500 border-t">
            <p>Generated on {new Date().toLocaleString()}</p>
            <p className="mt-1">{business.name || "ILKSOY GROUP, LLC"} - Shipment Tracking Document</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
